import type { StateCreator } from "zustand/vanilla";

import type { ImportGranularity, ItineraryImportPreview } from "@mountayaapp/types/api/api";

import type {
  ImportProviderId,
  ImportSession,
  ImportSubmissionStatus,
  ItineraryDraft,
} from "../importItinerary";

/**
 * ImportSessionSliceState is the open import shell slot.
 */
export interface ImportSessionSliceState {
  /**
   * importSession is the open import shell state (null = the shell is closed).
   * It holds the chosen provider, the input the user supplies (a transient File
   * for a file provider, an external id for an online one), the segmentation
   * granularity, the submission progress, the raw server preview, and the
   * editable draft projected from it. Set only through the tool bus, like every
   * other UiAction. The transient File handle lives on the session but is kept
   * out of every serializable schema (see importFileSchema), the same split the
   * videoExport slot makes for its non-serializable recording handles.
   */
  importSession: ImportSession | null;
}

/**
 * ImportSessionSliceActions are the whole-session open/close plus the narrow
 * field setters that fill an open import session in, reached only through the
 * tool bus.
 */
export interface ImportSessionSliceActions {
  /**
   * closeImport clears the import shell state (the slot becomes null). The whole
   * import session, including the transient File handle and the editable draft,
   * is dropped — the open/close tools drive this, the narrower setters update one
   * field of an already-open session.
   */
  closeImport: () => void;
  /**
   * openImport opens the import shell for the given provider, seeding a fresh
   * idle session (no file, no external id, an empty name, single-segment
   * granularity, no preview or draft yet). A second open replaces the prior session,
   * so re-opening for a different provider starts clean. The narrower setImport*
   * setters fill the session in as the user supplies input and the preview resolves.
   */
  openImport: (provider: ImportProviderId) => void;
  /**
   * removeImportDraftSegment drops one draft segment matched by client id from the
   * open session's draft. A no-op when the shell is closed or no draft is
   * projected; an unmatched id leaves the list unchanged.
   */
  removeImportDraftSegment: (segmentId: string) => void;
  /**
   * setImportActivity sets the resolved activity id on the open import session. A
   * no-op when the shell is closed, mirroring the narrow video setters.
   */
  setImportActivity: (activityId: string | null) => void;
  /**
   * setImportCollections sets the collections the imported itinerary will be added
   * to on the open import session. Like the name + privacy, it does not change what
   * the preview resolves, so the shell's collection picker writes it without
   * re-previewing; the confirm attaches them. A no-op when the shell is closed,
   * mirroring the other narrow import setters.
   */
  setImportCollections: (collectionIds: string[]) => void;
  /**
   * setImportDraft replaces the editable draft on the open import session — the
   * working copy the import editor mutates, projected from the preview. A no-op
   * when the shell is closed.
   */
  setImportDraft: (draft: ItineraryDraft | null) => void;
  /**
   * setImportDraftSegmentSpeed sets the suggested speed (km/h, null = use the
   * activity default) on one draft segment matched by client id, mutating the open
   * session's draft in place. A no-op when the shell is closed, no draft is
   * projected, or no segment matches — mirroring the narrow import setters.
   */
  setImportDraftSegmentSpeed: (segmentId: string, speed: number | null) => void;
  /**
   * setImportExternalId sets the external resource id an online provider imports
   * by, on the open import session. A no-op when the shell is closed.
   */
  setImportExternalId: (externalId: string | null) => void;
  /**
   * setImportFile sets the transient upload File on the open import session (null
   * clears it). The File is asserted as an instanceof File at the tool boundary
   * and never enters any serializable schema. A no-op when the shell is closed.
   */
  setImportFile: (file: File | null) => void;
  /**
   * setImportGranularity sets how the server slices the source into segments
   * (auto vs. single) on the open import session. A no-op when the shell is
   * closed.
   */
  setImportGranularity: (granularity: ImportGranularity) => void;
  /**
   * setImportName sets the editable itinerary name on the open import session. A
   * no-op when the shell is closed.
   */
  setImportName: (name: string) => void;
  /**
   * setImportOrganization sets the owning organization id on the open import
   * session (null lets the backend default to the authenticated user's org). Like
   * the name, it does not change what the preview resolves, so the shell's
   * OrganizationSelect writes it without re-previewing; the confirm reads it. A
   * no-op when the shell is closed, mirroring the other narrow import setters.
   */
  setImportOrganization: (organizationId: string | null) => void;
  /**
   * setImportPreview stores the raw server preview on the open import session
   * (null clears it). The editor consumes the projected draft, not this raw
   * payload; the preview is retained for re-projection. A no-op when the shell is
   * closed.
   */
  setImportPreview: (preview: ItineraryImportPreview | null) => void;
  /**
   * setImportPrivate sets whether the confirm persists the imported itinerary as
   * private, on the open import session. A no-op when the shell is closed,
   * mirroring the other narrow import setters.
   */
  setImportPrivate: (isPrivate: boolean) => void;
  /**
   * setImportProvider switches the chosen provider on the open import session. A
   * no-op when the shell is closed — opening a provider goes through openImport,
   * which seeds a fresh session.
   */
  setImportProvider: (provider: ImportProviderId) => void;
  /**
   * setImportStatus sets the submission progress (idle / previewing / ready /
   * confirming / error) on the open import session. A no-op when the shell is
   * closed.
   */
  setImportStatus: (status: ImportSubmissionStatus) => void;
}

/**
 * importSessionInitialState seeds the import shell closed.
 */
export const importSessionInitialState: ImportSessionSliceState = {
  importSession: null,
};

/**
 * createImportSessionSlice builds the import shell actions. openImport seeds a
 * fresh idle session; every narrow setter no-ops while the shell is closed so a
 * late field write never resurrects a closed shell.
 */
export const createImportSessionSlice: StateCreator<
  ImportSessionSliceState & ImportSessionSliceActions,
  [],
  [],
  ImportSessionSliceActions
> = (set) => ({
  closeImport: () => set({ importSession: null }),

  openImport: (provider) =>
    set({
      importSession: {
        activityId: null,
        // Seed no collections; the shell's picker fills them in and the confirm
        // attaches them after the create.
        collectionIds: [],
        draft: null,
        externalId: null,
        file: null,
        // Auto: the segmentation follows the recording's own structure — its stops,
        // its gaps, its turnarounds. "single" is reachable only through the tool, and
        // it is not a promise the pipeline can keep on a real track: the hard
        // boundaries and the routability sub-split run under it too, so a long
        // recording still arrives as many segments. Defaulting to the honest one.
        granularity: "auto",
        // Seed private to match the itinerary CREATE flow's default; the
        // shell's privacy Switch flips it before the confirm reads it.
        isPrivate: true,
        name: "",
        // Seed null; the shell's OrganizationSelect writes the resolved default
        // (the user's root org) once it lands. Null means the confirm omits
        // organization_id and the backend defaults to the auth org.
        organizationId: null,
        preview: null,
        provider,
        status: { kind: "idle" },
      },
    }),

  removeImportDraftSegment: (segmentId) =>
    set((state) => {
      const draft = state.importSession?.draft;
      if (!state.importSession || !draft) {
        return {};
      }

      return {
        importSession: {
          ...state.importSession,
          draft: {
            ...draft,
            segments: draft.segments.filter((segment) => segment.id !== segmentId),
          },
        },
      };
    }),

  setImportActivity: (activityId) =>
    set((state) =>
      state.importSession ? { importSession: { ...state.importSession, activityId } } : {},
    ),

  setImportCollections: (collectionIds) =>
    set((state) =>
      state.importSession ? { importSession: { ...state.importSession, collectionIds } } : {},
    ),

  setImportDraft: (draft) =>
    set((state) =>
      state.importSession ? { importSession: { ...state.importSession, draft } } : {},
    ),

  setImportDraftSegmentSpeed: (segmentId, speed) =>
    set((state) => {
      const draft = state.importSession?.draft;
      if (!state.importSession || !draft) {
        return {};
      }

      return {
        importSession: {
          ...state.importSession,
          draft: {
            ...draft,
            segments: draft.segments.map((segment) =>
              segment.id === segmentId ? { ...segment, speed } : segment,
            ),
          },
        },
      };
    }),

  setImportExternalId: (externalId) =>
    set((state) =>
      state.importSession ? { importSession: { ...state.importSession, externalId } } : {},
    ),

  setImportFile: (file) =>
    set((state) =>
      state.importSession ? { importSession: { ...state.importSession, file } } : {},
    ),

  setImportGranularity: (granularity) =>
    set((state) =>
      state.importSession ? { importSession: { ...state.importSession, granularity } } : {},
    ),

  setImportName: (name) =>
    set((state) =>
      state.importSession ? { importSession: { ...state.importSession, name } } : {},
    ),

  setImportOrganization: (organizationId) =>
    set((state) =>
      state.importSession ? { importSession: { ...state.importSession, organizationId } } : {},
    ),

  setImportPreview: (preview) =>
    set((state) =>
      state.importSession ? { importSession: { ...state.importSession, preview } } : {},
    ),

  setImportPrivate: (isPrivate) =>
    set((state) =>
      state.importSession ? { importSession: { ...state.importSession, isPrivate } } : {},
    ),

  setImportProvider: (provider) =>
    set((state) =>
      state.importSession ? { importSession: { ...state.importSession, provider } } : {},
    ),

  setImportStatus: (status) =>
    set((state) =>
      state.importSession ? { importSession: { ...state.importSession, status } } : {},
    ),
});
